import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { NotificationPreferenceChannel } from '#src/__generated__/globalTypes.ts';
import { SportOrShared } from '#src/__generated__/globalTypes.ts';
import { SportBall } from '#src/atoms/icons/SportBall.tsx';
import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { Tab, TabBar } from '#src/atoms/navigation/TabBar/index.tsx';
import { DisabledEmailWarning } from '#src/components/user/DisabledEmailWarning/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { navLabels } from '#src/lib/glossary.ts';
import {
  getSportFromSportOrShared,
  useFormattedSport,
} from '#src/lib/sports.ts';

import { NotificationPreference } from './NotificationPreference/index.tsx';
import { NotificationPreferenceLayout } from './NotificationPreferenceLayout/index.tsx';
import { NotificationSection } from './NotificationSection/index.tsx';
import { Section } from './Section/index.tsx';
import type {
  NotificationPreferencesQuery,
  NotificationPreferencesQueryVariables,
  UpdateMaxDailyPushNotificationsMutation,
  UpdateMaxDailyPushNotificationsMutationVariables,
  UpdateNotificationPreferencesMutation,
  UpdateNotificationPreferencesMutationVariables,
} from './__generated__/index.graphql.ts';
import { notificationSubtitles, notificationTitles } from './messages.ts';
import type {
  DisplayableMarketingPreference,
  DisplayablePreference,
} from './types.ts';
import {
  displayableMarketingPreferences as displayableMarketingPreferenceNames,
  displayablePreferences as displayablePreferenceNames,
} from './types.ts';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  align-items: start;
`;

const UPDATE_NOTIFICATION_PREFERENCES_MUTATION = gql`
  mutation UpdateNotificationPreferencesMutation(
    $input: updateUserSettingsInput!
    $sportOrShared: SportOrShared!
  ) {
    updateUserSettings(input: $input) {
      userSettings {
        id
        notificationPreferences(sport: $sportOrShared) {
          name
          ...NotificationPreference_notificationPreference
        }
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${NotificationPreference.fragments.notificationPreference}
` as TypedDocumentNode<
  UpdateNotificationPreferencesMutation,
  UpdateNotificationPreferencesMutationVariables
>;

const UPDATE_MAX_DAILY_PUSH_NOTIFICATIONS_MUTATION = gql`
  mutation UpdateMaxDailyPushNotificationsMutation(
    $input: updateUserSettingsInput!
  ) {
    updateUserSettings(input: $input) {
      userSettings {
        id
        maxDailyPushNotifications
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UpdateMaxDailyPushNotificationsMutation,
  UpdateMaxDailyPushNotificationsMutationVariables
>;

const NOTIFICATION_PREFERENCES_QUERY = gql`
  query NotificationPreferencesQuery($sportOrShared: SportOrShared) {
    currentUser {
      slug
      userSettings {
        id
        maxDailyPushNotifications
        notificationPreferences(sport: $sportOrShared) {
          name
          ...NotificationPreference_notificationPreference
        }
      }
    }
    notificationSections(forSettings: true) {
      slug
      ...NotificationSection_notificationSection
    }
  }
  ${NotificationPreference.fragments.notificationPreference}
  ${NotificationSection.fragments.notificationSection}
` as TypedDocumentNode<
  NotificationPreferencesQuery,
  NotificationPreferencesQueryVariables
>;

export const NotificationPreferences = () => {
  const formatSport = useFormattedSport();
  const { sport: sportFromContext } = useSportContext();
  const { formatMessage } = useIntl();
  const {
    flags: { useNewNotificationPreferences = false },
  } = useFeatureFlags();

  const [sport, setSport] = useState<SportOrShared>(sportFromContext);

  const [updateNotificationPreference] = useMutation(
    UPDATE_NOTIFICATION_PREFERENCES_MUTATION
  );
  const [updateMaxDailyPushNotifications] = useMutation(
    UPDATE_MAX_DAILY_PUSH_NOTIFICATIONS_MUTATION
  );

  const { data, loading } = useQuery(NOTIFICATION_PREFERENCES_QUERY, {
    variables: {
      sportOrShared: sport,
    },
  });

  const { currentUser, notificationSections } = data || {};
  const { notificationPreferences = [], maxDailyPushNotifications } =
    currentUser?.userSettings || {};

  const displayablePreferences = notificationPreferences.filter(preference =>
    displayablePreferenceNames.includes(
      preference.name as DisplayablePreference
    )
  );

  const displayableMarketingPreferences = notificationPreferences.filter(
    preference =>
      displayableMarketingPreferenceNames.includes(
        preference.name as DisplayableMarketingPreference
      )
  );

  const handleChange = (
    name: string,
    newValue: NotificationPreferenceChannel[] | Json
  ) => {
    updateNotificationPreference({
      variables: {
        input: {
          notificationPreference: {
            name,
            value: newValue,
            sport,
          },
        },
        sportOrShared: sport,
      },
    });
  };

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  if (
    !currentUser ||
    !notificationSections ||
    typeof maxDailyPushNotifications !== 'number'
  ) {
    return null;
  }

  return (
    <Wrapper>
      <TabBar value={sport} compact>
        {[
          SportOrShared.GLOBAL,
          SportOrShared.FOOTBALL,
          SportOrShared.NBA,
          SportOrShared.BASEBALL,
        ].map(s => (
          <Tab key={s} value={s} onClick={() => setSport(s)}>
            <SportBall sport={s} />
            {formatSport(getSportFromSportOrShared(s))}
          </Tab>
        ))}
      </TabBar>
      <DisabledEmailWarning />
      {useNewNotificationPreferences ? (
        <>
          {sport === SportOrShared.GLOBAL && (
            <Section
              title={
                <FormattedMessage
                  id="Settings.NotificationPreferences.preferences"
                  defaultMessage="Preferences"
                />
              }
            >
              <NotificationPreferenceLayout
                title={
                  <FormattedMessage
                    {...notificationTitles.max_push_notifications}
                  />
                }
                description={
                  <FormattedMessage
                    {...notificationSubtitles.max_push_notifications}
                  />
                }
              >
                <NativeSelect
                  value={maxDailyPushNotifications.toString()}
                  values={[10, 20, 30, 40, 50, 1000].map(v => {
                    const label =
                      v === 1000
                        ? formatMessage({
                            id: 'NotificationPreferences.maxPushNotifications.noLimit',
                            defaultMessage: 'No limit',
                          })
                        : v.toString();

                    return {
                      value: v.toString(),
                      label,
                    };
                  })}
                  onChange={v => {
                    updateMaxDailyPushNotifications({
                      variables: {
                        input: {
                          maxDailyPushNotifications: Number(v),
                        },
                      },
                    });
                  }}
                />
              </NotificationPreferenceLayout>
            </Section>
          )}
          {notificationSections.map(section => {
            return (
              <NotificationSection
                key={section.slug}
                notificationSection={section}
                sport={sport}
                onChange={handleChange}
              />
            );
          })}
        </>
      ) : (
        <>
          <Section title={<FormattedMessage {...navLabels.notifications} />}>
            {displayablePreferences.map(preference => (
              <NotificationPreference
                key={`${preference.name}-${sport}`}
                preference={preference}
                onChange={newValue => {
                  handleChange(preference.name, newValue);
                }}
              />
            ))}
          </Section>
          {displayableMarketingPreferences.length > 0 && (
            <Section
              title={
                <FormattedMessage
                  id="Settings.Notifications.marketingTitle"
                  defaultMessage="Marketing"
                />
              }
            >
              {displayableMarketingPreferences.map(preference => (
                <NotificationPreference
                  key={`${preference.name}-${sport}`}
                  preference={preference}
                  onChange={newValue => {
                    handleChange(preference.name, newValue);
                  }}
                />
              ))}
            </Section>
          )}
        </>
      )}
    </Wrapper>
  );
};
